import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { SlurryService, UpdateBatchRequestDto, UpsertMeasuredFoamRequestDto, UpsertMeasuredMaterialRequestDto, UpsertMeasuredMudRequestDto } from '../../../../services/slurry.service';
import { WebSerialScaleService } from '../../../../services/web-serial-scale.service';
import { DeveloperMeasureOverrideComponent } from '../developer-measure-override/developer-measure-override.component';

export interface BatchMeasureMaterialDialogData {
  measurementKind: 'material' | 'blend' | 'mud' | 'foam' | 'foamCup';
  mode: 'measure' | 'comments';
  commentKind?: 'weight' | 'container';
  slurryMaterialId: number | null;
  title: string;
  targetAmount: number | null;
  tolerance: number;
  preferredCisProcontainerId: string;
  preferredContainerDeleted: boolean;
  capturedContainerDeleted: boolean;
  canEditCoreValues: boolean;
  canEditComments: boolean;
  saveLabel: string;
  lockReason: string;
  slurryId: number;
  batchId: number;
  batchMixAsFoam: boolean;
  isFoamer: boolean;
  measuredAmount: number | null;
  overrideAmount: number | null;
  caputuredCisProcontainerId: string;
  containerComments: string;
  weightComments: string;
  replacePreferredCisProcontainerId: boolean;
  batchUpdateRequest: UpdateBatchRequestDto;
  showFluidLossFoamerWarning?: boolean;
  cupVolume?: number | null;
  existingFoamWeight?: number | null;
  existingCupWeight?: number | null;
  existingCupVolume?: number | null;
}

export interface BatchMeasureMaterialDialogResult {
  saved: boolean;
  createdMeasurement: boolean;
  measurementKind?: BatchMeasureMaterialDialogData['measurementKind'];
  material?: UpsertMeasuredMaterialRequestDto;
  blend?: UpdateBatchRequestDto;
  mud?: UpsertMeasuredMudRequestDto;
  foam?: UpsertMeasuredFoamRequestDto;
}

@Component({
  selector: 'app-batch-measure-material-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, DeveloperMeasureOverrideComponent],
  templateUrl: './batch-measure-material-dialog.component.html',
  styleUrls: ['./batch-measure-material-dialog.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class BatchMeasureMaterialDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly scannerInterKeyTimeoutMs = 500;
  private readonly scannerMaximumAverageInterKeyMs = 80;
  private readonly scannerMinimumLength = 4;
  private scannerBuffer = '';
  private scannerFirstCharacterAt: number | null = null;
  private scannerLastCharacterAt: number | null = null;
  private scannerBufferResetHandle: ReturnType<typeof setTimeout> | null = null;
  private scaleAbortController: AbortController | null = null;
  private destroyed = false;
  private barcodeCaptureCompleted = false;
  private weightCaptureCompleted = false;

  @ViewChild('capturedInput') private capturedInput?: ElementRef<HTMLInputElement>;
  @ViewChild('scannerInput') private scannerInput?: ElementRef<HTMLInputElement>;

  form = {
    measuredAmount: null as number | null,
    overrideAmount: null as number | null,
    caputuredCisProcontainerId: '',
    containerComments: '',
    weightComments: '',
    replacePreferredCisProcontainerId: false,
    cupVolume: null as number | null
  };

  errorMessage = '';
  scaleMessage = '';
  isCapturingBarcode = false;
  enterManually = false;
  developerManualEntryEnabled = false;
  readingWeight = false;
  isSaving = false;

  readonly isWebSerialSupported: boolean;

  constructor(
    private readonly dialogRef: MatDialogRef<BatchMeasureMaterialDialogComponent, BatchMeasureMaterialDialogResult>,
    @Inject(MAT_DIALOG_DATA) public readonly data: BatchMeasureMaterialDialogData,
    private readonly slurryService: SlurryService,
    private readonly webSerialScaleService: WebSerialScaleService
  ) {
    this.isWebSerialSupported = this.webSerialScaleService.isSupported;
    this.form.measuredAmount = data.measuredAmount;
    this.form.overrideAmount = data.overrideAmount;
    this.form.caputuredCisProcontainerId = data.caputuredCisProcontainerId;
    this.form.containerComments = data.containerComments;
    this.form.weightComments = data.weightComments;
    this.form.replacePreferredCisProcontainerId = data.replacePreferredCisProcontainerId;
    this.form.cupVolume = data.cupVolume ?? null;
    this.barcodeCaptureCompleted = !this.requiresBarcode || !!this.form.caputuredCisProcontainerId?.trim();
    this.weightCaptureCompleted = !this.showsWeight || this.form.measuredAmount != null;
    this.isCapturingBarcode = this.requiresBarcode && data.mode === 'measure' && data.canEditCoreValues && !this.form.caputuredCisProcontainerId?.trim();
  }

  ngOnInit(): void {
    // The scale selection belongs to the browser profile, not the server COM-port setting.
  }

  ngAfterViewInit(): void {
    this.focusScannerTarget();
    if (this.data.mode === 'measure' && this.data.canEditCoreValues && !this.requiresBarcode) {
      void this.readMeasuredWeight();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.scaleAbortController?.abort();
    this.clearScannerBuffer();
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.isCapturingBarcode || this.enterManually || this.isSaving || !this.data.canEditCoreValues) {
      return;
    }

    if (event.target === this.scannerInput?.nativeElement) {
      return;
    }

    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      this.commitScannerBuffer();
      return;
    }

    if (event.key.length !== 1 || event.ctrlKey || event.altKey || event.metaKey) {
      return;
    }

    event.preventDefault();
    this.appendScannerCharacter(event.key);
  }

  onScannerTargetKeydown(event: KeyboardEvent): void {
    if (!this.isCapturingBarcode || this.enterManually) {
      return;
    }

    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      this.commitScannerBuffer();
      return;
    }

    if (event.key.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey) {
      event.preventDefault();
      this.appendScannerCharacter(event.key);
    }
  }

  onScannerTargetInput(event: Event): void {
    if (!this.isCapturingBarcode || this.enterManually) {
      return;
    }

    const input = event.target as HTMLInputElement;
    const value = input.value.trim();
    input.value = '';
    if (value) {
      this.scannerBuffer = value;
      this.scannerFirstCharacterAt = Date.now();
      this.scannerLastCharacterAt = this.scannerFirstCharacterAt;
      this.restartScannerBufferReset(true, true);
    }
  }

  onScannerTargetPaste(event: ClipboardEvent): void {
    if (!this.isCapturingBarcode || this.enterManually) {
      return;
    }

    const pastedBarcode = event.clipboardData?.getData('text')?.trim();
    if (pastedBarcode) {
      event.preventDefault();
      this.acceptCapturedBarcode(pastedBarcode);
    }
  }

  get isContainerAcceptable(): boolean {
    if (!this.requiresBarcode) {
      return true;
    }
    const preferred = this.data.preferredCisProcontainerId;
    const captured = this.form.caputuredCisProcontainerId;
    if (!preferred?.trim() || !captured?.trim()) {
      return true;
    }
    return preferred
      .split(',')
      .map(v => v.trim())
      .filter(Boolean)
      .includes(captured);
  }

  get isWeightAcceptable(): boolean {
    const target = this.data.targetAmount;
    const measured = this.form.measuredAmount;
    if (target == null || measured == null) {
      return true;
    }
    return Math.abs(measured - target) <= this.data.tolerance;
  }

  get isFormValid(): boolean {
    if (this.data.mode === 'comments') {
      return true;
    }
    if (this.data.measurementKind === 'mud') {
      return this.barcodeCaptureCompleted;
    }
    if (this.data.measurementKind === 'foamCup') {
      return this.weightCaptureCompleted && this.form.cupVolume != null;
    }
    if (this.data.measurementKind === 'foam') {
      return this.weightCaptureCompleted;
    }
    return this.barcodeCaptureCompleted && this.weightCaptureCompleted;
  }

  get requiresBarcode(): boolean {
    return this.data.measurementKind === 'material' || this.data.measurementKind === 'blend' || this.data.measurementKind === 'mud';
  }

  get showsWeight(): boolean {
    return this.data.measurementKind !== 'mud';
  }

  get titlePrefix(): string {
    if (this.data.mode === 'comments') {
      return 'Measurement Comments';
    }
    switch (this.data.measurementKind) {
      case 'mud': return 'Measure Mud';
      case 'foam': return 'Measure Foam';
      case 'foamCup': return 'Measure Foam Cup';
      default: return 'Measure Batch Material';
    }
  }

  get canSave(): boolean {
    return !this.isSaving
      && !this.isCapturingBarcode
      && !this.readingWeight
      && (this.data.canEditCoreValues || this.data.canEditComments)
      && this.isFormValid;
  }

  get isWeightCommentMode(): boolean {
    return this.data.mode === 'comments' && this.data.commentKind === 'weight';
  }

  get activeComment(): string {
    return this.isWeightCommentMode ? this.form.weightComments : this.form.containerComments;
  }

  set activeComment(value: string) {
    if (this.isWeightCommentMode) {
      this.form.weightComments = value;
    } else {
      this.form.containerComments = value;
    }
  }

  get canEnterManually(): boolean {
    return this.data.canEditCoreValues
      && !this.isSaving
      && !this.readingWeight
      && (!this.enterManually || !!this.form.caputuredCisProcontainerId?.trim());
  }

  onEnterManually(): void {
    if (!this.data.canEditCoreValues || this.isSaving || this.readingWeight) {
      return;
    }

    if (!this.enterManually) {
      this.clearScannerBuffer();
      this.enterManually = true;
      this.focusScannerTarget();
      return;
    }

    const manualBarcode = this.form.caputuredCisProcontainerId;
    if (!manualBarcode?.trim()) {
      return;
    }

    this.acceptCapturedBarcode(manualBarcode);
  }

  // Developer manual entry handlers
  onDeveloperManualEntryChange(enabled: boolean): void {
    if (this.data.mode !== 'measure' || !this.data.canEditCoreValues || this.isSaving) {
      return;
    }

    this.developerManualEntryEnabled = enabled;
    this.clearScannerBuffer();

    if (enabled) {
      this.scaleAbortController?.abort();
      this.scaleAbortController = null;
      this.readingWeight = false;
      this.isCapturingBarcode = false;
      this.enterManually = false;
      this.scaleMessage = 'Developer manual entry enabled.';
      this.updateDeveloperCaptureState();
      return;
    }

    this.scaleMessage = '';
    this.updateDeveloperCaptureState();
    if (this.requiresBarcode && !this.barcodeCaptureCompleted) {
      this.isCapturingBarcode = true;
      this.focusScannerTarget();
    } else if (this.showsWeight && !this.weightCaptureCompleted) {
      void this.readMeasuredWeight();
    }
  }

  onDeveloperBarcodeChange(): void {
    if (!this.enterManually && !this.developerManualEntryEnabled) {
      return;
    }
    this.form.caputuredCisProcontainerId = this.form.caputuredCisProcontainerId?.trimStart() ?? '';
    if (!this.developerManualEntryEnabled) {
      return;
    }
    this.barcodeCaptureCompleted = !!this.form.caputuredCisProcontainerId?.trim();
  }

  onDeveloperWeightChange(): void {
    if (!this.developerManualEntryEnabled) {
      return;
    }
    this.weightCaptureCompleted = this.form.measuredAmount != null;
  }

  private updateDeveloperCaptureState(): void {
    this.barcodeCaptureCompleted = !this.requiresBarcode || !!this.form.caputuredCisProcontainerId?.trim();
    this.weightCaptureCompleted = !this.showsWeight || this.form.measuredAmount != null;
  }
  // End of developer manual entry handlers

  private acceptCapturedBarcode(barcode: string): void {
    this.form.caputuredCisProcontainerId = barcode.trimStart();
    this.barcodeCaptureCompleted = true;
    this.enterManually = false;
    this.isCapturingBarcode = false;
    this.clearScannerBuffer();
    if (this.data.measurementKind !== 'mud') {
      void this.readMeasuredWeight();
    }
  }

  private appendScannerCharacter(character: string): void {
    const now = Date.now();
    if (this.scannerLastCharacterAt != null && now - this.scannerLastCharacterAt > this.scannerInterKeyTimeoutMs) {
      this.clearScannerBuffer();
    }
    if (this.scannerFirstCharacterAt == null) {
      this.scannerFirstCharacterAt = now;
    }
    this.scannerLastCharacterAt = now;
    this.scannerBuffer += character;
    this.restartScannerBufferReset(true);
  }

  private commitScannerBuffer(allowWholeValue = false): void {
    const scannedBarcode = this.scannerBuffer.trim();
    const isScannerBurst = allowWholeValue || this.isLikelyScannerBurst();
    this.clearScannerBuffer();
    if (scannedBarcode && isScannerBurst) {
      this.acceptCapturedBarcode(scannedBarcode);
    }
  }

  private isLikelyScannerBurst(): boolean {
    if (this.scannerBuffer.length < this.scannerMinimumLength
      || this.scannerFirstCharacterAt == null
      || this.scannerLastCharacterAt == null) {
      return false;
    }

    const intervals = Math.max(1, this.scannerBuffer.length - 1);
    return (this.scannerLastCharacterAt - this.scannerFirstCharacterAt) / intervals
      <= this.scannerMaximumAverageInterKeyMs;
  }

  private focusScannerTarget(): void {
    setTimeout(() => {
      if (this.enterManually) {
        this.capturedInput?.nativeElement.focus();
      } else if (this.isCapturingBarcode) {
        this.scannerInput?.nativeElement.focus();
      }
    });
  }

  private restartScannerBufferReset(commitAfterTimeout = false, allowWholeValue = false): void {
    if (this.scannerBufferResetHandle) {
      clearTimeout(this.scannerBufferResetHandle);
    }
    this.scannerBufferResetHandle = setTimeout(() => {
      if (commitAfterTimeout) {
        this.commitScannerBuffer(allowWholeValue);
        return;
      }
      this.clearScannerBuffer();
    }, this.scannerInterKeyTimeoutMs);
  }

  private clearScannerBuffer(): void {
    if (this.scannerBufferResetHandle) {
      clearTimeout(this.scannerBufferResetHandle);
      this.scannerBufferResetHandle = null;
    }
    this.scannerBuffer = '';
    this.scannerFirstCharacterAt = null;
    this.scannerLastCharacterAt = null;
  }

  async readMeasuredWeight(): Promise<void> {
    if (!this.data.canEditCoreValues || this.readingWeight) {
      return;
    }

    if (!this.isWebSerialSupported) {
      this.scaleMessage = 'Scale reading requires Google Chrome or Microsoft Edge.';
      return;
    }

    this.scaleAbortController?.abort();
    const controller = new AbortController();
    this.scaleAbortController = controller;
    this.readingWeight = true;
    this.errorMessage = '';
    this.scaleMessage = this.data.measurementKind === 'foamCup'
      ? 'Please weigh the foam cup on the connected scale.'
      : this.data.measurementKind === 'foam'
        ? 'Please weigh the foam on the connected scale.'
        : 'Please weigh the material on the connected scale.';

    try {
      const result = await this.webSerialScaleService.readWeightFromGrantedPort(controller.signal);
      if (this.destroyed || controller.signal.aborted || this.scaleAbortController !== controller) {
        return;
      }
      if (!result.success || result.weight == null) {
        this.scaleMessage = result.errorMessage || 'Scale did not return a valid reading.';
        return;
      }

      this.form.measuredAmount = result.weight;
      this.weightCaptureCompleted = true;
      this.scaleMessage = `Measured stable weight loaded (${result.unit}).`;
    } catch (err: any) {
      if (this.destroyed || controller.signal.aborted || this.scaleAbortController !== controller) {
        return;
      }
      this.scaleMessage = err?.message || 'Failed to read weight from scale.';
    } finally {
      if (this.scaleAbortController === controller) {
        this.readingWeight = false;
        this.scaleAbortController = null;
      }
    }
  }

  retryScaleRead(): void {
    if ((this.requiresBarcode && !this.form.caputuredCisProcontainerId?.trim()) || this.readingWeight || this.data.mode !== 'measure') {
      return;
    }
    void this.readMeasuredWeight();
  }

  save(): void {
    if (!this.canSave) {
      return;
    }

    if (this.data.measurementKind === 'blend') {
      this.saveBlendMeasurement();
      return;
    }

    if (this.data.measurementKind === 'mud') {
      this.saveMudMeasurement();
      return;
    }

    if (this.data.measurementKind === 'foam' || this.data.measurementKind === 'foamCup') {
      this.saveFoamMeasurement();
      return;
    }

    if (this.data.slurryMaterialId == null) {
      this.errorMessage = 'The measured material is missing.';
      return;
    }

    const request: UpsertMeasuredMaterialRequestDto = {
      slurryMaterialId: this.data.slurryMaterialId,
      measuredAmount: this.form.measuredAmount ?? undefined,
      overrideAmount: this.form.overrideAmount ?? undefined,
      caputuredCisProcontainerId: this.form.caputuredCisProcontainerId || undefined,
      isFoamer: this.data.batchMixAsFoam && this.data.isFoamer,
      containerComments: this.form.containerComments || undefined,
      weightComments: this.form.weightComments || undefined,
      preferredCisProcontainerId: this.data.preferredCisProcontainerId || undefined,
      replacePreferredCisProcontainerId: this.form.replacePreferredCisProcontainerId
    };

    this.dialogRef.close({
      saved: true,
      createdMeasurement: this.data.mode === 'measure',
      measurementKind: 'material',
      material: request
    });
  }

  private saveBlendMeasurement(): void {
    const blend: UpdateBatchRequestDto = {
      ...this.data.batchUpdateRequest,
      measuredBlendWeight: this.form.measuredAmount ?? undefined,
      capturedBlendCisProcontainerId: this.form.caputuredCisProcontainerId || undefined,
      blendContainerComments: this.form.containerComments || undefined,
      blendWeightComments: this.form.weightComments || undefined,
      replacePreferredBlendCisProcontainerId: this.form.replacePreferredCisProcontainerId
    };
    this.dialogRef.close({ saved: true, createdMeasurement: this.data.mode === 'measure', measurementKind: 'blend', blend });
  }

  private saveMudMeasurement(): void {
    const request: UpsertMeasuredMudRequestDto = {
      measuredMudCisProcontainer: this.form.caputuredCisProcontainerId || undefined,
      mudContainerComments: this.form.containerComments || undefined,
      preferredMudCisProcontainer: this.data.preferredCisProcontainerId || undefined,
      replacePreferredMudCisProcontainer: this.form.replacePreferredCisProcontainerId
    };

    this.dialogRef.close({ saved: true, createdMeasurement: this.data.mode === 'measure', measurementKind: 'mud', mud: request });
  }

  private saveFoamMeasurement(): void {
    const request: UpsertMeasuredFoamRequestDto = this.data.measurementKind === 'foam'
      ? {
          measuredWeight: this.form.measuredAmount ?? undefined,
          cupWeight: this.data.existingCupWeight ?? undefined,
          cupVolume: this.data.existingCupVolume ?? undefined,
          weightComments: this.form.weightComments || undefined
        }
      : {
          measuredWeight: this.data.existingFoamWeight ?? undefined,
          cupWeight: this.form.measuredAmount ?? undefined,
          cupVolume: this.form.cupVolume ?? undefined,
          weightComments: this.form.weightComments || undefined
        };

    this.dialogRef.close({
      saved: true,
      createdMeasurement: this.data.mode === 'measure',
      measurementKind: this.data.measurementKind,
      foam: request
    });
  }

  cancel(): void {
    this.scaleAbortController?.abort();
    this.dialogRef.close({ saved: false, createdMeasurement: false });
  }
}
